import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { JOB_STEPS } from "../CreateJobPage";
import { Check, Minus } from "lucide-react";
import {
  JobFormValues,
  parseAmount,
  splitLines,
} from "../schema/jobForm.schema";
import {
  COMPENSATION_TYPE_OPTIONS,
  CURRENCY_SYMBOLS,
  findLabel,
  JOB_TYPE_OPTIONS,
  WORKPLACE_OPTIONS,
} from "../schema/jobForm.options";

const TABS = [
  {
    label: "Job Description",
    value: 1,
  },
  {
    label: "Skills and Experience",
    value: 2,
  },
];

const SKILL_COLUMNS = [
  { name: "importance", label: "Importance" },
  { name: "requiredLevel", label: "Level" },
  { name: "experience", label: "Experience" },
  { name: "recency", label: "Recency" },
] as const;

const Step5 = ({ activeStep }) => {
  const [activeTab, setActiveTab] = useState(1);
  const { control } = useFormContext<JobFormValues>();
  const values = useWatch({ control }) as JobFormValues;

  const symbol = CURRENCY_SYMBOLS[values.currency] ?? "";
  const payUnit = COMPENSATION_TYPE_OPTIONS.find(
    (option) => option.value === values.compensationType,
  )?.unit;
  const pay =
    values.salaryMin && values.salaryMax
      ? `${symbol}${parseAmount(values.salaryMin).toLocaleString()}–${symbol}${parseAmount(values.salaryMax).toLocaleString()} ${payUnit}`
      : null;
  const responsibilities = splitLines(values.keyResponsibilities);
  const skills = values.skills ?? [];

  const publicationChecks = [
    {
      label: "Hiring company and role facts confirmed",
      isDone: Boolean(values.companyId && values.title),
    },
    {
      label: "Candidate-facing description reviewed",
      isDone: Boolean(values.jobOverview),
    },
    {
      label: `${skills.length} contextual skill requirement${skills.length === 1 ? "" : "s"} included`,
      isDone: skills.length > 0,
    },
    {
      label: "Requirement source retained for agency records",
      isDone:
        values.creationStrategy === 'Manual_Job_Post' || Boolean(values.jobRequirementId),
    },
  ];

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {JOB_STEPS[activeStep - 1]?.tagline}
        </span>
      </div>
      {/* Job details */}
      <div className="flex flex-col rounded-md border overflow-hidden mt-5">
        <div className="flex justify-between p-4 border-b">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-md bg-charcoal flex items-center justify-center font-bold text-md text-white uppercase">
              {(values.companyName || values.title)?.charAt(0)}
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-lg">{values.title}</span>
              <span className="font-bold text-xss">
                {values.showCompanyDetails
                  ? values.companyName
                  : "Confidential hiring company"}
              </span>
              <div className="flex items-center gap-4">
                <span className="text-xss font-medium text-light-black">
                  {[
                    values.location,
                    findLabel(WORKPLACE_OPTIONS, values.workPlaceOption),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span className="text-xss font-medium text-light-black">
                  {findLabel(JOB_TYPE_OPTIONS, values.jobType)}
                </span>
                {pay ? (
                  <span className="text-xss font-medium text-light-black">
                    {pay}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="rounded-full uppercase border font-bold text-xsss px-2 py-1 text-slate-600 h-max">
            candidate preview
          </div>
        </div>

        <div className="flex items-center border-b p-4 gap-8">
          {TABS?.map((tab) => {
            const isActiveTab = tab?.value === activeTab;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab?.value)}
                className={`font-bold text-xss ${isActiveTab ? "underline" : " text-light-black"}`}
              >
                {tab?.label}
              </button>
            );
          })}
        </div>

        {activeTab === 1 ? (
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-2">
              <span className="font-bold text-xss uppercase tracking-widest text-slate-600">
                Published job description
              </span>
              <span className="font-medium text-xss text-light-black whitespace-pre-line">
                {values.jobOverview}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-bold text-sm text-black">
                Key responsibilities
              </span>
              <ul className="list-disc pl-4 font-medium text-xss text-light-black">
                {responsibilities.map((responsibility, index) => (
                  <li key={index}>{responsibility}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col">
              <span className="font-semibold text-sm text-black">
                Skills and experience requested for this role
              </span>
              <span className="font-medium text-xsss text-light-black">
                These structured requirements power COMTRACKED matching and
                remain explainable to candidates.
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {skills.map((skill, index) => {
                return (
                  <div
                    key={index}
                    className="grid grid-cols-[1.8fr_0.8fr_0.8fr_0.8fr_0.8fr] items-center border-b py-2.5"
                  >
                    <div className="flex flex-col pr-1.5">
                      <span className="font-semibold text-xs text-black">
                        {skill?.title}
                      </span>
                      <span className="text-xss text-light-black font-medium">
                        {skill?.usageContext}
                      </span>
                    </div>
                    {SKILL_COLUMNS.map((column) => (
                      <div
                        key={column.name}
                        className="flex flex-col border-l pl-2.5 h-max"
                      >
                        <span className="font-bold text-xsss text-light-black uppercase tracking-wider">
                          {column.label}
                        </span>
                        <span className="font-bold text-xss text-black">
                          {skill?.[column.name]}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Publication checks */}
      <div className="flex items-center px-4 py-3.5 rounded-md border bg-slate-50 mt-5 gap-10">
        <span className="font-bold text-xss text-light-black uppercase tracking-wide">
          Publication checks
        </span>
        <div className="grid grid-cols-2 gap-1 col-span-5 gap-x-10">
          {publicationChecks.map((check) => {
            return (
              <div
                key={check.label}
                className={`flex items-center text-xss font-medium gap-1 ${check.isDone ? "text-light-black" : "text-orange-700"}`}
              >
                {check.isDone ? (
                  <Check size={12} strokeWidth={3.5} />
                ) : (
                  <Minus size={12} strokeWidth={3.5} />
                )}
                {check.label}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Step5;
